import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter, useNavigate } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { Toaster, type ToasterProps } from "sonner";
import { Merge } from "type-fest";

import { routeTree } from "./routeTree.gen";
import {
  DialogDisplay,
  TANSTACK_QUERY_CLIENT,
  isNativeApplication,
  requestPushNotificationPermission,
} from "./services";
import { css } from "./styled-system/css";

const router = createRouter({ routeTree, scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function Application() {
  return (
    <ProviderWrapper>
      <RootComponent />
    </ProviderWrapper>
  );
}

function ProviderWrapper({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider attribute="class" disableTransitionOnChange>
      <QueryClientProvider client={TANSTACK_QUERY_CLIENT}>{children}</QueryClientProvider>
    </ThemeProvider>
  );
}

const TOASTER_ICONS = {
  error: null,
  info: null,
  success: null,
  warning: null,
} satisfies ToasterProps["icons"];

function RootComponent() {
  const translation = useTranslation();

  // ネイティブアプリケーションの場合はプッシュ通知権限を要求する。
  usePushNotificationPermission();

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      {translation.ready && <RouterProvider router={router} />}
      <Toaster
        className={css({
          fontFamily: '"Inter", "Noto Sans JP", "Noto Color Emoji", serif !important',
          "--error-text": "#FEF2F2 !important",
          "--error-bg": "#DC2626 !important",
          "--error-border": "#E4E4E7 !important",
          "--info-text": "#FEF2F2 !important",
          "--info-bg": "#0284C7 !important",
          "--info-border": "#E4E4E7 !important",
          "--normal-text": "#18181B !important",
          "--normal-bg": "#F4F4F5 !important",
          "--normal-border": "#E4E4E7 !important",
          "--success-text": "#FEF2F2 !important",
          "--success-bg": "#0D9488 !important",
          "--success-border": "#E4E4E7 !important",
          "--warning-text": "#FEF2F2 !important",
          "--warning-bg": "#CA8A04 !important",
          "--warning-border": "#E4E4E7 !important",

          "&[data-sonner-toaster] [data-sonner-toast]": {
            fontSize: "14px !important",
            fontWeight: 500 + " !important",
            lineHeight: 10 / 7 + " !important",
          },
        })}
        duration={5_000}
        icons={TOASTER_ICONS}
        richColors
        visibleToasts={8}
      />
      <DialogDisplay />
    </ErrorBoundary>
  );
}

type ErrorPageProperties = Merge<FallbackProps, Record<never, never>>;
function ErrorPage({ error }: ErrorPageProperties) {
  const navigate = useNavigate();

  return (
    <div>
      <p>{(error as any)?.message}</p>
      <button
        onClick={function () {
          navigate({ to: "/login" });
        }}
      >
        LOGIN PAGE
      </button>
    </div>
  );
}

/**
 * プッシュ通知権限を使用する。
 * ネイティブアプリケーションで起動された場合は権限を要求し、
 * ユーザーが許可または拒否するまで待機する。
 * @returns プッシュ通知権限
 */
function usePushNotificationPermission() {
  const promise = useRef<Promise<void> | void>(undefined);
  const [granted, setGranted] = useState<boolean | null | undefined>();
  const [error, setError] = useState<unknown>();

  useEffect(function () {
    if (!isNativeApplication()) {
      // ネイティブアプリケーションから起動されていない場合は実行しない。
      setGranted(null);
    } else if (!promise.current) {
      // 複数回実行するとエラーが発生するため、一度だけ実行する。
      promise.current = requestPushNotificationPermission()
        .then(function (granted) {
          setGranted(granted);
        })
        .catch(function (error) {
          setError(error);
        });
    }
  }, []);

  return useMemo(
    function () {
      const loading = granted === undefined && error === undefined;
      return { error, granted, loaded: !loading, loading };
    },
    [error, granted],
  );
}
